import { createStore } from '@xstate/store';
import { useSelector } from '@xstate/store/react';
import { createContext, useContext, useMemo, useRef } from 'react';

type SetStateFn<State> = (
  partial: Partial<State> | ((prev: State) => Partial<State>),
) => void;

type GetStateFn<State> = () => State;

type StoreInitializer<State> = (
  set: SetStateFn<State>,
  get: GetStateFn<State>,
) => State;

type StoreApi<State> = {
  setState: SetStateFn<State>;
  getState: GetStateFn<State>;
};

/**
 * Creates a non-global @xstate/store that is initialized from React context.
 * Drop-in replacement for createZustandContext with the same API shape.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createXStateStoreContext = <
  State extends Record<string, any>,
>() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type InternalStore = ReturnType<typeof createStore<State, any>>;

  const StoreContext = createContext<InternalStore | undefined>(undefined);

  const sendUpdate = (store: InternalStore, updates: Partial<State>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    store.send({ type: 'update', updates } as any);
  };

  const StoreProvider: React.FC<{
    initializer: StoreInitializer<State>;
    children: React.ReactNode;
  }> = ({ children, initializer }) => {
    const storeRef = useRef<InternalStore | undefined>(undefined);

    if (!storeRef.current) {
      const holder: { instance: InternalStore | undefined } = {
        instance: undefined,
      };

      const set: SetStateFn<State> = (partial) => {
        if (!holder.instance) return;
        const current = holder.instance.getSnapshot().context;
        const updates =
          typeof partial === 'function' ? partial(current) : partial;
        sendUpdate(holder.instance, updates);
      };

      const get: GetStateFn<State> = () => {
        if (!holder.instance) {
          throw new Error('Store not initialized');
        }
        return holder.instance.getSnapshot().context;
      };

      const initialState = initializer(set, get);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      holder.instance = createStore(initialState as any, {
        update: (ctx: State, event: { updates: Partial<State> }) =>
          ({ ...ctx, ...event.updates }) as State,
      }) as unknown as InternalStore;

      storeRef.current = holder.instance;
    }

    return (
      <StoreContext.Provider value={storeRef.current}>
        {children}
      </StoreContext.Provider>
    );
  };

  const useStoreApi = (): StoreApi<State> => {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error('Missing StoreProvider');
    }

    return useMemo(
      () => ({
        setState: (
          partial: Partial<State> | ((prev: State) => Partial<State>),
        ) => {
          const current = store.getSnapshot().context;
          const updates =
            typeof partial === 'function' ? partial(current) : partial;
          sendUpdate(store, updates);
        },
        getState: () => store.getSnapshot().context,
      }),
      [store],
    );
  };

  function useStoreFromContext<T>(selector: (state: State) => T): T {
    const store = useContext(StoreContext);
    if (!store) throw new Error('Missing StoreProvider');
    return useSelector(store, (s) => selector(s.context));
  }

  return { StoreProvider, useStore: useStoreFromContext, useStoreApi };
};
